import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineDocumentText, HiOutlineCalendar, HiOutlineArrowDownTray, HiOutlineArrowLeft } from 'react-icons/hi2';
import { authService, reportService, machineService, plantService, lineService } from '../services';
import { usePermissions } from '../hooks/usePermissions';
import {
  showReportGeneratingToast,
  reportGenerationDone,
  reportGenerationFailed,
} from '../utils/reportGenerationToast';
import './Reports.css';

const MTBRReport = () => {
  const navigate = useNavigate();
  const tenantId = authService.getTenantId();
  const { hasPermission } = usePermissions();
  
  // Permission check
  const canView = hasPermission('MTBR.View');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState([]);
  const [machines, setMachines] = useState([]);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    machineId: '',
    plantId: '',
  }); 
  const [plants, setPlants] = useState([]);
  const [lines, setLines] = useState([]);
  React.useEffect(() => {
    if (tenantId && canView) {
      loadInitialData();
    } else if (!canView) {
      setError('You do not have permission to view MTBR reports');
    } else {
      setError('Please login to view MTBR reports');
    }
  }, [tenantId, canView]);

  const loadInitialData = async () => {
    try {
      const [machinesData, plantsData, linesData] = await Promise.all([
        machineService.getAllMachines(),
        plantService.getAllPlants(),
        lineService.getAllLines(),
      ]);
      setMachines(machinesData || []);
      setPlants(plantsData || []);
      setLines(linesData || []);

      // Auto-select plant if only 1 exists
      if (plantsData?.length === 1) {
        setFilters(prev => ({ ...prev, plantId: plantsData[0].plantId.toString() }));
      }
    } catch (err) {
      console.error('Error loading initial data:', err);
    }
  };

  const handleGenerateReport = async () => {
    if (!canView) {
      setError('You do not have permission to view MTBR reports');
      return;
    }
    
    if (!filters.startDate || !filters.endDate) {
      setError('Please select both start date and end date');
      return;
    }

    showReportGeneratingToast();
    try {
      setLoading(true);
      setError('');
      
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999); // Include full end date

      const machineId = filters.machineId ? parseInt(filters.machineId, 10) : null;
      let data = await reportService.getMTBRReport(startDate, endDate, machineId);
      
      if (filters.plantId && !machineId) {
        data = (data || []).filter(row => {
          const machine = machines.find(m => m.machineName === row.machineName || m.machineCode === row.machineCode);
          if (!machine) return true;
          const line = lines.find(l => l.lineId === machine.lineId);
          return machine.plantId?.toString() === filters.plantId || line?.plantId?.toString() === filters.plantId;
        });
      }
      setReportData(data || []);
      
      if (!data || data.length === 0) {
        setError('No data found for the selected criteria');
      }
      reportGenerationDone();
    } catch (err) {
      console.error('Error generating report:', err);
      const msg = err.response?.data?.message || 'Failed to generate report. Please try again.';
      setError(msg);
      reportGenerationFailed(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = () => {
    if (!canView) {
      setError('You do not have permission to view MTBR reports');
      return;
    }
    
    if (!reportData || reportData.length === 0) {
      setError('Please generate the report first before exporting');
      return;
    }

    try {
      const headers = Object.keys(reportData[0]);
      const filename = `MTBR_Report_${filters.startDate}_to_${filters.endDate}.csv`;
      reportService.exportToCSV(reportData, headers, filename);
    } catch (err) {
      console.error('Error exporting report:', err);
      setError('Failed to export report. Please try again.');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => {
        const next = { ...prev, [name]: value };
        if (name === 'plantId') {
            next.machineId = '';
            if (value) localStorage.setItem('defaultPlantId', value);
        }
        return next;
    });
  };

  return (
    <div className="pagecontainer report-page">
      <div className="page-header">
        <div className="header-content">
          <div className="report-title-section">
            <button className="back-btn" onClick={() => navigate("/home/reports")} title="Back to Reports Dashboard">
              <HiOutlineArrowLeft />
            </button>
            <h1 className='d-flex align-items-center gap-2'>
              <HiOutlineDocumentText className="header-icon" />
              MTBR Report (Mean Time Between Repair)
            </h1>
          </div>
          <p>View and analyze Mean Time Between Repair metrics</p>
        </div>
        <div className="header-actions">

          <button className="btn-primary" onClick={handleExportReport}>
            <HiOutlineArrowDownTray />
            Export
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {canView ? (
        <>
          {/* Filter Section */}
          <div className='reportbox'>
          <div className="report-filters">
            <div className="filter-group">
              <label>Start Date</label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                className="filter-input"
              />
            </div>
            <div className="filter-group">
              <label>End Date</label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                className="filter-input"
              />
            </div>
            <div className="filter-group">
              <label>Plant</label>
              <select
                name="plantId"
                value={filters.plantId}
                onChange={handleFilterChange}
                className="filter-input"
              >
                <option value="">All Plants</option>
                {plants.map(plant => (
                  <option key={plant.plantId} value={plant.plantId}>
                    {plant.plantName}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Machine (Optional)</label>
              <select
                name="machineId"
                value={filters.machineId}
                onChange={handleFilterChange}
                className="filter-input"
                disabled={!filters.plantId}
              >
                <option value="">All Machines</option>
                {machines.filter(m => !filters.plantId || lines.find(l => l.lineId === m.lineId)?.plantId?.toString() === filters.plantId).map(machine => (
                  <option key={machine.machineId} value={machine.machineId}>
                    {machine.machineName} ({machine.machineCode})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="report-filter-actions">
            {(filters.plantId || filters.machineId) && (
              <button 
                className="oee-clear-filters-btn" 
                onClick={() => {
                  setFilters({
                    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
                    endDate: new Date().toISOString().split('T')[0],
                    plantId: '',
                    machineId: ''
                  });
                  setReportData([]);
                }}
              >
                ✕ Clear Filters
              </button>
            )}
            <button type="button" className="btn-primary" onClick={handleGenerateReport} disabled={loading}>
              <HiOutlineCalendar /> Generate Report
            </button>
          </div>
        </div>

          <div className="report-container">
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading report data...</p>
          </div>
        ) : reportData.length === 0 ? (
          <div className="report-content">
            <div className="report-placeholder">
              <HiOutlineDocumentText className="placeholder-icon" />
              <h3>MTBR Report</h3>
              <p>Select date range and click "Generate Report" to view data</p>
            </div>
          </div>
        ) : (
          <div className="report-content">
            <div className="report-table-wrapper">
              <table className="report-table">
                <thead>
                  <tr>
                    {Object.keys(reportData[0]).map(header => (
                      <th key={header}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((row, index) => (
                    <tr key={index}>
                      {Object.values(row).map((cell, cellIndex) => (
                        <td key={cellIndex}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="report-summary">
              <p><strong>Total Records:</strong> {reportData.length}</p>
            </div>
          </div>
        )}
      </div>
        </>
      ) : (
        <div className="report-container">
          <div className="report-content">
            <div className="report-placeholder">
              <HiOutlineDocumentText className="placeholder-icon" />
              <h3>Access Denied</h3>
              <p>You do not have permission to view MTBR reports</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MTBRReport;

